import { ChannelTypeEnum, ChatProviderIdEnum } from '@novu/shared';
import { useEffect, useRef, useState } from 'react';
import { RiSearchLine } from 'react-icons/ri';
import { useNavigate, useParams } from 'react-router-dom';
import { saveCrmEmailProvider } from '@/api/crm-email-providers';
import {
  CampaignSendingFields,
  EMPTY_SENDING_DRAFT,
  isSendingConfigured,
  toSendingSettings,
} from '@/components/crm/campaign-sending-settings';
import { t } from '@/components/crm/crm-i18n';
import { useEnvironment } from '@/context/environment/hooks';
import { useCreateIntegration } from '@/hooks/use-create-integration';
import { useFetchIntegrations } from '@/hooks/use-fetch-integrations';
import { showErrorToast, showSuccessToast } from '../../../components/primitives/sonner-helpers';
import { useSetPrimaryIntegration } from '../../../hooks/use-set-primary-integration';
import { buildRoute, ROUTES } from '../../../utils/routes';
import { Button } from '../../primitives/button';
import { Input } from '../../primitives/input';
import { UnsavedChangesAlertDialog } from '../../unsaved-changes-alert-dialog';
import { IntegrationFormData } from '../types';
import { ChannelTabs } from './channel-tabs';
import { useIntegrationList } from './hooks/use-integration-list';
import { useIntegrationPrimaryModal } from './hooks/use-integration-primary-modal';
import { useSidebarNavigationManager } from './hooks/use-sidebar-navigation-manager';
import { IntegrationSettings } from './integration-settings';
import { IntegrationSheet } from './integration-sheet';
import { SelectPrimaryIntegrationModal } from './modals/select-primary-integration-modal';
import { handleIntegrationError } from './utils/handle-integration-error';
import { cleanCredentials } from './utils/helpers';

export type CreateIntegrationSidebarProps = {
  isOpened: boolean;
};

export function CreateIntegrationSidebar({ isOpened }: CreateIntegrationSidebarProps) {
  const navigate = useNavigate();
  const { providerId } = useParams();

  const { mutateAsync: createIntegration, isPending } = useCreateIntegration();
  const { mutateAsync: setPrimaryIntegration, isPending: isSettingPrimary } = useSetPrimaryIntegration();
  const [formState, setFormState] = useState({ isValid: true, errors: {} as Record<string, unknown>, isDirty: false });
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(isOpened);

  const handleIntegrationSelect = (integrationId: string) => {
    navigate(buildRoute(ROUTES.INTEGRATIONS_CONNECT_PROVIDER, { providerId: integrationId }), { replace: true });
  };

  const handleBack = () => {
    navigate(ROUTES.INTEGRATIONS_CONNECT, { replace: true });
  };

  const { selectedIntegration, step, searchQuery, setSearchQuery, onIntegrationSelect, onBack } =
    useSidebarNavigationManager({
      isOpened,
      initialProviderId: providerId,
      onIntegrationSelect: handleIntegrationSelect,
      onBack: handleBack,
    });

  const { catalogProviders, integrationsByChannel } = useIntegrationList(searchQuery);
  const provider = catalogProviders.find((providerItem) => providerItem.id === (selectedIntegration || providerId));

  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpened && step === 'select') {
      const timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpened, step]);

  // While the user is on the Telegram configure step, the sidebar shows a QR
  // mobile-setup card. If the visitor completes setup on their phone, the
  // public consume endpoint creates a new Telegram integration server-side.
  // We detect that creation by snapshotting the set of existing Telegram
  // integration ids on first paint and watching for a new one to appear.
  const isTelegramCreateStep = provider?.id === ChatProviderIdEnum.Telegram && step === 'configure';
  const initialTelegramIdsRef = useRef<Set<string> | null>(null);
  const hasHandledAutoConnectRef = useRef<boolean>(false);

  // Reset the snapshot whenever the user leaves the Telegram configure step so
  // re-entering the flow takes a fresh baseline (instead of treating already
  // existing integrations as freshly created).
  useEffect(() => {
    if (!isTelegramCreateStep) {
      initialTelegramIdsRef.current = null;
      hasHandledAutoConnectRef.current = false;
    }
  }, [isTelegramCreateStep]);

  const { integrations } = useFetchIntegrations({
    refetchInterval: isTelegramCreateStep ? 3000 : undefined,
  });

  useEffect(() => {
    if (!isTelegramCreateStep || !integrations) return;

    if (initialTelegramIdsRef.current === null) {
      initialTelegramIdsRef.current = new Set(
        integrations
          .filter((integration) => integration.providerId === ChatProviderIdEnum.Telegram)
          .map((integration) => integration._id)
      );

      return;
    }

    if (hasHandledAutoConnectRef.current) return;

    const newOne = integrations.find(
      (integration) =>
        integration.providerId === ChatProviderIdEnum.Telegram && !initialTelegramIdsRef.current?.has(integration._id)
    );

    if (newOne) {
      // Latch immediately so a refetch firing before unmount can't replay the
      // toast + navigate for the same newly-detected integration.
      hasHandledAutoConnectRef.current = true;
      showSuccessToast('Telegram bot connected from your phone');
      // Direct close (skip `useUnsavedChangesAlertDialog`) — the user explicitly
      // opted into the mobile flow, so confirming "discard changes" is noisy.
      setIsSheetOpen(false);
      navigate(buildRoute(ROUTES.INTEGRATIONS_UPDATE, { integrationId: newOne._id }));
    }
  }, [isTelegramCreateStep, integrations, navigate]);
  const {
    isPrimaryModalOpen,
    setIsPrimaryModalOpen,
    pendingData,
    handleSubmitWithPrimaryCheck,
    handlePrimaryConfirm,
    existingPrimaryIntegration,
    isChannelSupportPrimary,
  } = useIntegrationPrimaryModal({
    onSubmit: handleCreateIntegration,
    integrations,
    channel: provider?.channel,
    mode: 'create',
  });

  // izipush-crm : réglages d'envoi des campagnes, saisis dans la même fiche qu'une intégration email.
  const { environments } = useEnvironment();
  const [sending, setSending] = useState(EMPTY_SENDING_DRAFT);

  async function handleCreateIntegration(data: IntegrationFormData) {
    if (!provider) return;

    try {
      const integration = await createIntegration({
        providerId: provider.id,
        channel: provider.channel,
        credentials: cleanCredentials(data.credentials),
        configurations: data.configurations,
        name: data.name,
        identifier: data.identifier,
        active: data.active,
        _environmentId: data.environmentId,
      });

      if (data.primary && isChannelSupportPrimary && data.active) {
        await setPrimaryIntegration({ integrationId: integration.data._id });
      }

      if (provider.channel === ChannelTypeEnum.EMAIL && isSendingConfigured(sending)) {
        const environment = environments?.find((candidate) => candidate._id === data.environmentId);
        if (environment) {
          await saveCrmEmailProvider({
            environment,
            integrationId: integration.data._id,
            body: toSendingSettings(sending),
          }).catch((error: unknown) => showErrorToast((error as Error).message, t('sending.toast.failed')));
        }
      }

      showSuccessToast('Integration created successfully');

      setIsSheetOpen(false);
      navigate(ROUTES.INTEGRATIONS);
    } catch (error: unknown) {
      handleIntegrationError(error, 'create');
    }
  }

  // Sync sheet open state with isOpened prop
  useEffect(() => {
    setIsSheetOpen(isOpened);
  }, [isOpened]);

  const handleClose = () => {
    // Only check for unsaved changes if we're on the configure step (form is visible)
    if (step === 'configure' && formState.isDirty && !isPending && !isSettingPrimary) {
      setShowUnsavedDialog(true);

      return;
    }

    setIsSheetOpen(false);
    navigate(ROUTES.INTEGRATIONS);
  };

  const handleProceedClose = () => {
    setShowUnsavedDialog(false);
    setIsSheetOpen(false);
    navigate(ROUTES.INTEGRATIONS);
  };

  const handleCancelClose = () => {
    setShowUnsavedDialog(false);
  };

  return (
    <>
      <IntegrationSheet
        isOpened={isSheetOpen}
        onClose={handleClose}
        provider={provider}
        mode="create"
        step={step}
        onBack={onBack}
      >
        {step === 'select' ? (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b p-3">
              <Input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search providers across channels..."
                leadingIcon={RiSearchLine}
                size="xs"
              />
            </div>
            <div className="scrollbar-custom flex-1 overflow-y-auto">
              <ChannelTabs
                integrationsByChannel={integrationsByChannel}
                searchQuery={searchQuery}
                onIntegrationSelect={onIntegrationSelect}
              />
            </div>
          </div>
        ) : provider ? (
          <>
            <div className="scrollbar-custom flex-1 overflow-y-auto">
              <IntegrationSettings
                isChannelSupportPrimary={isChannelSupportPrimary}
                provider={provider}
                onSubmit={handleSubmitWithPrimaryCheck}
                mode="create"
                onFormStateChange={setFormState}
              />
              {provider.channel === ChannelTypeEnum.EMAIL && (
                <CampaignSendingFields
                  value={sending}
                  onChange={setSending}
                  footer={<p className="text-text-soft text-paragraph-xs">{t('sending.createHint')}</p>}
                />
              )}
            </div>
            <div className="bg-background flex justify-end gap-2 border-t p-3">
              <Button
                type="submit"
                variant="secondary"
                form={`integration-configuration-form-${provider.id}`}
                isLoading={isPending || isSettingPrimary}
                size="xs"
                disabled={!formState.isValid}
              >
                Create Integration
              </Button>
            </div>
          </>
        ) : null}
      </IntegrationSheet>

      <SelectPrimaryIntegrationModal
        isOpen={isPrimaryModalOpen}
        onOpenChange={setIsPrimaryModalOpen}
        onConfirm={handlePrimaryConfirm}
        currentPrimaryName={existingPrimaryIntegration?.name}
        newPrimaryName={pendingData?.name ?? ''}
        isLoading={isPending || isSettingPrimary}
      />

      <UnsavedChangesAlertDialog show={showUnsavedDialog} onCancel={handleCancelClose} onProceed={handleProceedClose} />
    </>
  );
}
